import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import offer from '../offer.json';
import { service_catalog_items } from '../../../../utils/offers/service_catalog';
import { Actions_, FormBox, FormBoxTitle, Grey_, Input_, Main_, Partial_Loader, Toggle_ } from 'monica-alexandria';
import { calculate_offer_totals } from '../utils/calculate_offer_totals';
import { offer_catalog_type_key } from '../utils/offer_creation_rules';
import { create_personalized_ads_checkout_link } from '../../../../services/api/Offers';
import {
    creative_fixed_one_off_value,
    marketing_fixed_one_off_value,
    service_price_value,
    unlocked_service_values
} from '../utils/marketing_service_rules';

const NewOfferPrice = styled.fieldset`
    display: grid;
    gap: 2rem;
    border: 0;
    margin: 0;
    padding: 0;
    min-width: 0;

    &:disabled {
        pointer-events: none;
    }
`;

const NewOfferPriceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 2rem;
`;

const NewOfferPriceError = styled.p`
    color: var(--error);
`;

const NewOfferPriceHint = styled.p`
    color: ${p => p.theme.mid};
`;

const website_maintenance_payment_links = {
    website_maintenance_starter: 'https://buy.stripe.com/7sIbJ605Ie39cow7vq',
    website_maintenance_business: 'https://buy.stripe.com/5kA8wU9Gi7EL74c3fp'
};

const known_service_type_keys = ['website_creation', 'website_maintenance', 'marketing', 'graphic', 'creative', 'sass', 'website'];

const type_title_by_key = (typeName = '') => {
    if (typeName === 'marketing') {
        return 'Personalised ads';
    }

    if (typeName === 'graphic' || typeName === 'creative') {
        return 'One-offs';
    }

    if (typeName === 'website_creation') {
        return 'Website creation';
    }

    if (typeName === 'website_maintenance') {
        return 'Website maintenance';
    }

    if (typeName === 'website') {
        return 'Website';
    }

    if (typeName === 'sass') {
        return 'SASS';
    }

    return typeName;
};

const service_type_from_key = (serviceKey = '', selectedTypes = []) => {
    const typeFromSelectedTypes = selectedTypes.find((typeName) => serviceKey.startsWith(`${typeName}_`));

    if (typeFromSelectedTypes) {
        return typeFromSelectedTypes;
    }

    const typeFromKnownKeys = known_service_type_keys.find((typeName) => serviceKey.startsWith(`${typeName}_`));

    if (typeFromKnownKeys) {
        return typeFromKnownKeys;
    }

    return serviceKey.split('_')[0];
};

const fixed_one_off_value_by_service_key = (serviceKey = '') => {
    const marketingFixedValue = marketing_fixed_one_off_value(serviceKey);

    if (marketingFixedValue !== null) {
        return marketingFixedValue;
    }

    const creativeFixedValue = creative_fixed_one_off_value(serviceKey);

    if (creativeFixedValue !== null) {
        return creativeFixedValue;
    }

    if (serviceKey === 'website_creation_eshop' || serviceKey === 'website_eshop' || serviceKey === 'website_plan_eshop') {
        return true;
    }

    if (serviceKey.startsWith('website_creation_custom_')) {
        return true;
    }

    if (serviceKey.startsWith('website_creation_plan_') || serviceKey.startsWith('website_plan_')) {
        return true;
    }

    if (serviceKey.startsWith('website_maintenance_')) {
        return false;
    }

    return null;
};

const selected_website_maintenance_link = (services = []) => {
    const selectedMaintenanceService = services.find((service) => service.serviceKey.startsWith('website_maintenance_'));

    if (!selectedMaintenanceService) {
        return '';
    }

    return `${selectedMaintenanceService.servicePaymentLinkDefault || website_maintenance_payment_links[selectedMaintenanceService.serviceKey] || ''}`.trim();
};

const to_decimal_number = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const parsedValue = Number(String(value).replace(',', '.'));

    if (Number.isNaN(parsedValue)) {
        return null;
    }

    return parsedValue;
};

const clamp_discount_percentage = (value) => {
    const parsedValue = Number(value) || 0;

    if (parsedValue > 100) {
        return 100;
    }

    return parsedValue;
};

const setup_services_for_price = (selectedTypes, setupData) => {
    const selectedServices = setupData?.selectedServices || {};
    const customServicesByType = setupData?.customServicesByType || {};

    const defaultServices = service_catalog_items().map(({ serviceKey, sourceType, catalogType, serviceValue }) => ({
        serviceKey,
        sourceType,
        catalogType,
        serviceText: serviceValue.text,
        servicePriceDefault: typeof serviceValue.price === 'number' ? serviceValue.price : serviceValue.price?.suggested ?? 15,
        serviceOneOffDefault: serviceValue.one_off ?? false,
        serviceVatIncludedDefault: serviceValue.vat_included ?? false,
        servicePaymentLinkDefault: serviceValue.payment_link || ''
    }));

    const customServices = selectedTypes.flatMap((typeName) => {
        if (typeName !== 'website_creation') {
            return [];
        }

        return (customServicesByType[typeName] || []).map((service) => {
            return {
                serviceKey: service.serviceKey,
                serviceText: service.text,
                servicePriceDefault: typeof service.price === 'number' ? service.price : 15,
                serviceOneOffDefault: true,
                serviceVatIncludedDefault: false,
                servicePaymentLinkDefault: ''
            };
        });
    });

    return [...defaultServices, ...customServices].filter((service) => selectedServices[service.serviceKey]);
};

export default function New_Offer_Price(props) {
    const { t } = useTranslation();
    const [recurringPaymentLinkUrl, setRecurringPaymentLinkUrl] = useState(
        props.priceData?.recurringPaymentLinkUrl ?? props.priceData?.paymentLinkUrl ?? ''
    );
    const [recurringPaymentUsesBankTransfer, setRecurringPaymentUsesBankTransfer] = useState(
        props.priceData?.recurringPaymentUsesBankTransfer === true
        || `${props.priceData?.recurringPaymentMethod || ''}`.trim().toLowerCase() === 'bank_transfer'
    );
    const [oneOffPaymentLinkUrl, setOneOffPaymentLinkUrl] = useState(
        props.priceData?.oneOffPaymentLinkUrl ?? props.priceData?.paymentLinkUrl ?? ''
    );
    const [recurringPaymentLinkUrlError, setRecurringPaymentLinkUrlError] = useState('');
    const [generatingCheckoutLink, setGeneratingCheckoutLink] = useState(false);
    const [vatEnabled, setVatEnabled] = useState(props.priceData?.vatEnabled ?? true);
    const [vatRate, setVatRate] = useState(`${props.priceData?.vatRate ?? offer.vat}`);
    const [validFor, setValidFor] = useState(`${props.priceData?.validFor ?? offer.valid_for}`);
    const [typeDiscountValues, setTypeDiscountValues] = useState(props.priceData?.typeDiscountValues ?? {});
    const [marketingTotalValue, setMarketingTotalValue] = useState(props.priceData?.marketingTotalValue ?? null);
    const [restTypeTotalValues, setRestTypeTotalValues] = useState(props.priceData?.restTypeTotalValues ?? {});
    const [mixedRecurringTotalValue, setMixedRecurringTotalValue] = useState(
        props.priceData?.mixedRecurringTotalValue
            ?? (Number.isFinite(Number(props.priceData?.mixedRecurringManualNetAmount))
                ? `${props.priceData?.mixedRecurringManualNetAmount}`
                : '')
    );
    const [mixedOneOffTotalValue, setMixedOneOffTotalValue] = useState(
        props.priceData?.mixedOneOffTotalValue
            ?? (Number.isFinite(Number(props.priceData?.mixedOneOffManualNetAmount))
                ? `${props.priceData?.mixedOneOffManualNetAmount}`
                : '')
    );

    useEffect(() => {
        setRecurringPaymentLinkUrl(props.priceData?.recurringPaymentLinkUrl ?? props.priceData?.paymentLinkUrl ?? '');
        setRecurringPaymentUsesBankTransfer(
            props.priceData?.recurringPaymentUsesBankTransfer === true
            || `${props.priceData?.recurringPaymentMethod || ''}`.trim().toLowerCase() === 'bank_transfer'
        );
        setOneOffPaymentLinkUrl(props.priceData?.oneOffPaymentLinkUrl ?? props.priceData?.paymentLinkUrl ?? '');
        setRecurringPaymentLinkUrlError('');
        setVatEnabled(props.priceData?.vatEnabled ?? true);
        setVatRate(`${props.priceData?.vatRate ?? offer.vat}`);
        setValidFor(`${props.priceData?.validFor ?? offer.valid_for}`);
        setTypeDiscountValues(props.priceData?.typeDiscountValues ?? {});
        setMarketingTotalValue(props.priceData?.marketingTotalValue ?? null);
        setRestTypeTotalValues(props.priceData?.restTypeTotalValues ?? {});
        setMixedRecurringTotalValue(
            props.priceData?.mixedRecurringTotalValue
                ?? (Number.isFinite(Number(props.priceData?.mixedRecurringManualNetAmount))
                    ? `${props.priceData?.mixedRecurringManualNetAmount}`
                    : '')
        );
        setMixedOneOffTotalValue(
            props.priceData?.mixedOneOffTotalValue
                ?? (Number.isFinite(Number(props.priceData?.mixedOneOffManualNetAmount))
                    ? `${props.priceData?.mixedOneOffManualNetAmount}`
                    : '')
        );
    }, [props.priceData]);

    const rawSelectedTypes = props.typesData?.selectedTypes?.length
        ? props.typesData.selectedTypes
        : props.selectedTypes?.length
            ? props.selectedTypes
            : [];
    const selectedTypes = [...new Set(rawSelectedTypes.map(offer_catalog_type_key))];
    const selectedServices = setup_services_for_price(selectedTypes, props.setupData || {});
    const selectedWebsiteMaintenancePaymentLink = selected_website_maintenance_link(selectedServices);

    useEffect(() => {
        if (!selectedWebsiteMaintenancePaymentLink.length) {
            return;
        }

        setRecurringPaymentLinkUrl(selectedWebsiteMaintenancePaymentLink);
        setRecurringPaymentUsesBankTransfer(false);
        setRecurringPaymentLinkUrlError('');
    }, [selectedWebsiteMaintenancePaymentLink]);

    const servicePriceValues = props.setupData?.servicePriceOverwriteValues || {};
    const serviceOneOffValues = props.setupData?.serviceOneOffValues || {};
    const pricedServices = selectedServices.map((service) => {
        const priceValue = service_price_value(service.serviceKey, service.servicePriceDefault, servicePriceValues);
        const fixedOneOffValue = fixed_one_off_value_by_service_key(service.serviceKey);
        const serviceOneOffValue = fixedOneOffValue === null
            ? serviceOneOffValues[service.serviceKey] ?? service.serviceOneOffDefault
            : fixedOneOffValue;
        const serviceType = service.catalogType || service_type_from_key(service.serviceKey, selectedTypes);
        const serviceName = service.serviceKey.replace(`${service.sourceType || serviceType}_`, '');

        return {
            ...service,
            servicePrice: priceValue,
            servicePriceNumber: Number(priceValue) || 0,
            serviceOneOff: serviceOneOffValue,
            serviceVatIncluded: service.serviceVatIncludedDefault ?? false,
            serviceType,
            serviceName
        };
    });
    const hasRecurringServices = pricedServices.some((service) => !service.serviceOneOff);
    const hasRecurringMarketingServices = pricedServices.some((service) => service.serviceType === 'marketing' && !service.serviceOneOff);
    const hasSelectedOneOffServices = pricedServices.some((service) => service.serviceOneOff);
    const oneOffServicesAreFree = hasRecurringServices && hasSelectedOneOffServices;
    const hasOneOffServices = hasSelectedOneOffServices && !oneOffServicesAreFree;
    const hasOneOffOnlyServices = hasOneOffServices && !hasRecurringServices;
    const hasMixedPaymentTypes = hasRecurringServices && hasOneOffServices;
    const vatRateValue = vatEnabled ? Number(vatRate) || 0 : 0;
    const visibleTypes = [...new Set([...selectedTypes, ...pricedServices.map((service) => service.serviceType)])].filter((typeName) => {
        return pricedServices.some((service) => service.serviceType === typeName);
    });
    const baseCalculatedTotals = calculate_offer_totals({
        pricedServices,
        typeDiscountValues: {},
        vatEnabled,
        vatRate: vatRateValue
    });
    const baseTotalsByType = baseCalculatedTotals.totalsByType || {};
    const resolvedTypeDiscountValues = visibleTypes.reduce((resolvedLookup, typeName) => {
        const baseSubtotalBeforeDiscount = Number(
            baseTotalsByType[typeName]?.billableSubtotalBeforeDiscount
            ?? baseTotalsByType[typeName]?.subtotalBeforeDiscount
            ?? 0
        );
        const manualTotalInput = typeName === 'marketing'
            ? marketingTotalValue
            : restTypeTotalValues[typeName];
        const manualTotalValue = to_decimal_number(manualTotalInput);
        const shouldUseTypeManualTotal = !hasMixedPaymentTypes;

        if (shouldUseTypeManualTotal
            && manualTotalInput !== ''
            && manualTotalInput !== null
            && manualTotalInput !== undefined
            && manualTotalValue !== null) {
            const discountInPercentage = baseSubtotalBeforeDiscount > 0
                ? ((baseSubtotalBeforeDiscount - manualTotalValue) / baseSubtotalBeforeDiscount) * 100
                : 0;

            return {
                ...resolvedLookup,
                [typeName]: clamp_discount_percentage(discountInPercentage)
            };
        }

        return {
            ...resolvedLookup,
            [typeName]: clamp_discount_percentage(typeDiscountValues[typeName])
        };
    }, {});
    const calculatedTotals = calculate_offer_totals({
        pricedServices,
        typeDiscountValues: resolvedTypeDiscountValues,
        vatEnabled,
        vatRate: vatRateValue
    });
    const totalsByType = calculatedTotals.totalsByType || {};
    const marketingTotals = totalsByType.marketing || {
        subtotalBeforeDiscount: 0,
        standaloneSubtotal: 0,
        discountPercent: 0,
        discountAmount: 0,
        totalAfterDiscount: 0
    };
    const marketingTotalDiscountInEuros = marketingTotals.standaloneSubtotal - marketingTotals.totalAfterDiscount;
    const marketingTotalDiscountInPercentage = marketingTotals.standaloneSubtotal > 0
        ? (marketingTotalDiscountInEuros / marketingTotals.standaloneSubtotal) * 100
        : 0;
    const mixedRecurringManualTotal = to_decimal_number(mixedRecurringTotalValue);
    const mixedOneOffManualTotal = to_decimal_number(mixedOneOffTotalValue);
    const hasMixedRecurringManualTotal = hasMixedPaymentTypes
        && mixedRecurringTotalValue !== ''
        && mixedRecurringTotalValue !== null
        && mixedRecurringTotalValue !== undefined
        && mixedRecurringManualTotal !== null;
    const hasMixedOneOffManualTotal = hasMixedPaymentTypes
        && mixedOneOffTotalValue !== ''
        && mixedOneOffTotalValue !== null
        && mixedOneOffTotalValue !== undefined
        && mixedOneOffManualTotal !== null;
    const recurringSubtotalAfterDiscount = hasMixedRecurringManualTotal
        ? Math.max(0, mixedRecurringManualTotal)
        : calculatedTotals.recurringNetAmount;
    const oneOffSubtotalAfterDiscount = hasMixedOneOffManualTotal
        ? Math.max(0, mixedOneOffManualTotal)
        : calculatedTotals.oneOffNetAmount;
    const recurringBaseSubtotalBeforeDiscount = baseCalculatedTotals.recurringNetAmount;
    const oneOffBaseSubtotalBeforeDiscount = oneOffServicesAreFree
        ? baseCalculatedTotals.oneOffSubtotalBeforeDiscount
        : baseCalculatedTotals.oneOffNetAmount;
    const recurringDiscountAmount = Math.max(0, recurringBaseSubtotalBeforeDiscount - recurringSubtotalAfterDiscount);
    const oneOffDiscountAmount = Math.max(0, oneOffBaseSubtotalBeforeDiscount - oneOffSubtotalAfterDiscount);
    const recurringDiscountPercent = recurringBaseSubtotalBeforeDiscount > 0
        ? clamp_discount_percentage((recurringDiscountAmount / recurringBaseSubtotalBeforeDiscount) * 100)
        : 0;
    const oneOffDiscountPercent = oneOffBaseSubtotalBeforeDiscount > 0
        ? clamp_discount_percentage((oneOffDiscountAmount / oneOffBaseSubtotalBeforeDiscount) * 100)
        : 0;
    const subtotal = hasMixedPaymentTypes
        ? recurringBaseSubtotalBeforeDiscount + oneOffBaseSubtotalBeforeDiscount
        : calculatedTotals.subtotalBeforeDiscount;
    const discountAmount = hasMixedPaymentTypes
        ? recurringDiscountAmount + oneOffDiscountAmount
        : calculatedTotals.totalDiscountAmount;
    const totalAfterDiscount = recurringSubtotalAfterDiscount + oneOffSubtotalAfterDiscount;
    const recurringVatAmount = vatEnabled ? recurringSubtotalAfterDiscount * vatRateValue : 0;
    const oneOffVatAmount = vatEnabled ? oneOffSubtotalAfterDiscount * vatRateValue : 0;
    const vatAmount = recurringVatAmount + oneOffVatAmount;
    const recurringTotalWithVat = recurringSubtotalAfterDiscount + recurringVatAmount;
    const oneOffTotalWithVat = oneOffSubtotalAfterDiscount + oneOffVatAmount;
    const total = recurringTotalWithVat + oneOffTotalWithVat;

    const change_type_discount = (typeName, value) => {
        setTypeDiscountValues((previousDiscounts) => {
            return {
                ...previousDiscounts,
                [typeName]: value
            };
        });
    };

    const change_marketing_total = (value) => {
        setMarketingTotalValue(value);

        if (value === '') {
            change_type_discount('marketing', 0);
            return;
        }

        const nextTotal = Number(String(value).replace(',', '.'));

        if (Number.isNaN(nextTotal)) {
            return;
        }

        const subtotalValue = baseTotalsByType?.marketing?.billableSubtotalBeforeDiscount
            ?? baseTotalsByType?.marketing?.subtotalBeforeDiscount
            ?? 0;
        const discountInPercentage = subtotalValue > 0 ? ((subtotalValue - nextTotal) / subtotalValue) * 100 : 0;

        change_type_discount('marketing', discountInPercentage);
    };

    const change_rest_type_total = (typeName, value) => {
        setRestTypeTotalValues((previousTotals) => {
            return {
                ...previousTotals,
                [typeName]: value
            };
        });

        if (value === '') {
            change_type_discount(typeName, 0);
            return;
        }

        const nextTotal = Number(String(value).replace(',', '.'));

        if (Number.isNaN(nextTotal)) {
            return;
        }

        const subtotalValue = baseTotalsByType[typeName]?.billableSubtotalBeforeDiscount
            ?? baseTotalsByType[typeName]?.subtotalBeforeDiscount
            ?? 0;
        const discountInPercentage = subtotalValue > 0 ? ((subtotalValue - nextTotal) / subtotalValue) * 100 : 0;

        change_type_discount(typeName, discountInPercentage);
    };

    const change_vat_rate = (value) => {
        if (value === '') {
            setVatRate('');
            return;
        }

        if (!/^(0(\.\d{0,2})?|1(\.0{0,2})?)$/.test(value)) {
            return;
        }

        setVatRate(value);
    };

    const go_to_next = async() => {
        if (generatingCheckoutLink) {
            return;
        }

        if (props.fakeOffer) {
            props.onNext({
                hasRecurringServices, hasOneOffServices, validFor: Number(validFor) || 0,
                vatEnabled: false, vatRate: 0, typeDiscountValues: {}, servicePriceOverwriteValues: {},
                paymentLinkUrl: '', recurringPaymentLinkUrl: '', oneOffPaymentLinkUrl: '',
                recurringPaymentMethod: 'internal', oneOffPaymentMethod: 'internal',
                totalNetAmount: 0, recurringNetAmount: 0, oneOffNetAmount: 0,
                totalVatAmount: 0, recurringVatAmount: 0, oneOffVatAmount: 0,
                recurringTotalWithVat: 0, oneOffTotalWithVat: 0, subtotal: 0, totalDiscountAmount: 0, total: 0
            });
            return;
        }

        let formattedRecurringPaymentLinkUrl = hasRecurringServices && !recurringPaymentUsesBankTransfer ? recurringPaymentLinkUrl.trim() : '';
        if (hasRecurringMarketingServices && !recurringPaymentUsesBankTransfer) {
            setGeneratingCheckoutLink(true);
            setRecurringPaymentLinkUrlError('');
            try {
                const response = await create_personalized_ads_checkout_link({
                    amount: Math.round(recurringSubtotalAfterDiscount * 100),
                    lang: props.typesData?.offerLanguage || 'el'
                });
                formattedRecurringPaymentLinkUrl = response.data.data.url;
                setRecurringPaymentLinkUrl(formattedRecurringPaymentLinkUrl);
            } catch (error) {
                setRecurringPaymentLinkUrlError(t('Could not generate the checkout link. Check that the recurring net price is at least EUR 0.50 and try again.'));
                return;
            } finally {
                setGeneratingCheckoutLink(false);
            }
        }

        const formattedOneOffPaymentLinkUrl = hasOneOffOnlyServices || oneOffServicesAreFree ? '' : oneOffPaymentLinkUrl.trim();
        const hasRecurringPaymentLinkError = hasRecurringServices && !recurringPaymentUsesBankTransfer && !formattedRecurringPaymentLinkUrl.length;
        const usesBankTransferForOneOff = hasOneOffOnlyServices || (hasOneOffServices && !formattedOneOffPaymentLinkUrl.length);
        const fallbackPaymentLinkUrl = hasRecurringServices && !hasOneOffServices
            ? formattedRecurringPaymentLinkUrl
            : !hasRecurringServices && hasOneOffServices
                ? formattedOneOffPaymentLinkUrl
                    : '';

        setRecurringPaymentLinkUrlError(hasRecurringPaymentLinkError ? t('Recurring payment link is required.') : '');

        if (hasRecurringPaymentLinkError) {
            return;
        }

        if (props.onNext) {
            props.onNext({
                paymentLinkUrl: fallbackPaymentLinkUrl,
                recurringPaymentLinkUrl: formattedRecurringPaymentLinkUrl,
                recurringPaymentMethod: hasRecurringServices
                    ? (recurringPaymentUsesBankTransfer ? 'bank_transfer' : 'payment_link')
                    : '',
                recurringPaymentUsesBankTransfer: hasRecurringServices && recurringPaymentUsesBankTransfer,
                oneOffPaymentLinkUrl: formattedOneOffPaymentLinkUrl,
                oneOffPaymentMethod: hasOneOffServices
                    ? (usesBankTransferForOneOff ? 'bank_transfer' : 'payment_link')
                    : '',
                oneOffPaymentUsesBankTransfer: hasOneOffServices && usesBankTransferForOneOff,
                vatEnabled,
                vatRate: Number(vatRate) || 0,
                typeDiscountValues: hasMixedPaymentTypes ? {} : resolvedTypeDiscountValues,
                marketingTotalValue,
                restTypeTotalValues,
                mixedRecurringTotalValue: hasMixedPaymentTypes ? mixedRecurringTotalValue : '',
                mixedOneOffTotalValue: hasMixedPaymentTypes ? mixedOneOffTotalValue : '',
                mixedRecurringManualNetAmount: hasMixedPaymentTypes && hasMixedRecurringManualTotal
                    ? Math.max(0, mixedRecurringManualTotal)
                    : null,
                mixedOneOffManualNetAmount: hasMixedPaymentTypes && hasMixedOneOffManualTotal
                    ? Math.max(0, mixedOneOffManualTotal)
                    : null,
                mixedRecurringBaseNetAmount: hasMixedPaymentTypes ? recurringBaseSubtotalBeforeDiscount : null,
                mixedOneOffBaseNetAmount: hasMixedPaymentTypes ? oneOffBaseSubtotalBeforeDiscount : null,
                mixedRecurringDiscountPercent: hasMixedPaymentTypes ? recurringDiscountPercent : null,
                mixedOneOffDiscountPercent: hasMixedPaymentTypes ? oneOffDiscountPercent : null,
                mixedRecurringDiscountAmount: hasMixedPaymentTypes ? recurringDiscountAmount : null,
                mixedOneOffDiscountAmount: hasMixedPaymentTypes ? oneOffDiscountAmount : null,
                validFor: Number(validFor) || 0,
                servicePriceOverwriteValues: unlocked_service_values(servicePriceValues),
                hasRecurringServices,
                hasOneOffServices,
                totalNetAmount: totalAfterDiscount,
                recurringNetAmount: recurringSubtotalAfterDiscount,
                oneOffNetAmount: oneOffSubtotalAfterDiscount,
                totalVatAmount: vatAmount,
                recurringVatAmount,
                oneOffVatAmount,
                recurringTotalWithVat,
                oneOffTotalWithVat,
                subtotal,
                totalDiscountAmount: discountAmount,
                total
            });
        }
    };

    return (
        <NewOfferPrice disabled={generatingCheckoutLink}>
            {props.allowFakeOffer ? <NewOfferPriceRow>
                <p>{t('Fake offer')}</p>
                <Toggle_ id="fake-offer" active={props.fakeOffer} onToggle={() => props.onFakeOfferChange(!props.fakeOffer)} />
            </NewOfferPriceRow> : null}
            {props.fakeOffer ? <p>{t('Fake offers total €0 and activate services without recording a payment.')}</p> : null}
            {!props.fakeOffer ? <>
            {visibleTypes.includes('marketing') ? (
                <FormBox>
                    <FormBoxTitle>
                        <h4>{t('Personalised ads')}</h4>
                    </FormBoxTitle>
                    <NewOfferPriceRow>
                        <p>{t('Original price')}</p>
                        <h5>€ {marketingTotals.standaloneSubtotal.toFixed(2)}</h5>
                    </NewOfferPriceRow>
                    <NewOfferPriceRow>
                        <p>{t('Price after discount')}</p>
                        <h5>€ {`${marketingTotalDiscountInEuros.toFixed(2)} / ${marketingTotalDiscountInPercentage.toFixed(0)}%`}</h5>
                    </NewOfferPriceRow>
                    {!hasMixedPaymentTypes ? (
                        <NewOfferPriceRow>
                            <p>{t('Price after discount')}</p>
                            <Input_ 
                                placeholder={t('Total new price')}
                                type="number"
                                step="0.01"
                                value={marketingTotalValue === null ? `${marketingTotals.totalAfterDiscount}` : marketingTotalValue}
                                onChange={(event) => change_marketing_total(event.target.value)}
                            />
                        </NewOfferPriceRow>
                    ) : (
                        <NewOfferPriceHint>{t('Use separate recurring and one-off manual totals below.')}</NewOfferPriceHint>
                    )}
                </FormBox>
            ) : null}
            {visibleTypes.filter((typeName) => typeName !== 'marketing').map((typeName) => {
                const typeTotals = totalsByType[typeName] || {
                    standaloneSubtotal: 0,
                    totalAfterDiscount: 0
                };
                const typeTotalDiscountInEuros = typeTotals.standaloneSubtotal - typeTotals.totalAfterDiscount;
                const typeTotalDiscountInPercentage = typeTotals.standaloneSubtotal > 0
                    ? (typeTotalDiscountInEuros / typeTotals.standaloneSubtotal) * 100
                    : 0;

                return (
                    <FormBox key={typeName}>
                        <FormBoxTitle>
                            <h4>{t(type_title_by_key(typeName))}</h4>
                        </FormBoxTitle>
                        <NewOfferPriceRow>
                            <p>{t('Original price')}</p>
                            <h5>€ {typeTotals.standaloneSubtotal.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('Price after discount')}</p>
                            <h5>€ {`${typeTotalDiscountInEuros.toFixed(2)} / ${typeTotalDiscountInPercentage.toFixed(0)}%`}</h5>
                        </NewOfferPriceRow>
                        {!hasMixedPaymentTypes ? (
                            <NewOfferPriceRow>
                                <p>{t('Price after discount')}</p>
                                <Input_
                                    placeholder={t('Total new price')}
                                    type="number"
                                    step="0.01"
                                    value={restTypeTotalValues[typeName] ?? `${typeTotals.totalAfterDiscount}`}
                                    onChange={(event) => change_rest_type_total(typeName, event.target.value)}
                                />
                            </NewOfferPriceRow>
                        ) : (
                            <NewOfferPriceHint>{t('Use separate recurring and one-off manual totals below.')}</NewOfferPriceHint>
                        )}
                    </FormBox>
                );
            })}
            <FormBox>
                <FormBoxTitle>
                    <h4>{t('VAT and validity')}</h4>
                </FormBoxTitle>
                {oneOffServicesAreFree ? (
                    <NewOfferPriceHint>{t('One-off services are free of charge because this offer includes recurring services.')}</NewOfferPriceHint>
                ) : null}
                <NewOfferPriceRow>
                    <p>{t("Vat applies")}</p>
                    <Toggle_
                        active={vatEnabled}
                        onToggle={() => setVatEnabled(!vatEnabled)}
                    />
                </NewOfferPriceRow>
                <NewOfferPriceRow>
                    <p>{t("Vat")} {vatRate * 100}%</p>
                    <Input_
                        type="number"
                        value={vatRate}
                        onChange={(event) => change_vat_rate(event.target.value)}
                        min="0"
                        max="1"
                        step="0.01"
                        disabled={!vatEnabled}
                    />
                </NewOfferPriceRow>
                <NewOfferPriceRow>
                    <p>{t('Offer lifespan (Days)')}</p>
                    <Input_
                        value={validFor}
                        onChange={(event) => setValidFor(event.target.value)}
                    />
                </NewOfferPriceRow>
                {hasRecurringServices ? (
                    <>
                        <NewOfferPriceRow>
                            <p>{t('Recurring via bank transfer')}</p>
                            <Toggle_
                                active={recurringPaymentUsesBankTransfer}
                                onToggle={() => {
                                    setRecurringPaymentUsesBankTransfer(!recurringPaymentUsesBankTransfer);
                                    setRecurringPaymentLinkUrlError('');
                                }}
                            />
                        </NewOfferPriceRow>
                        {hasRecurringMarketingServices && !recurringPaymentUsesBankTransfer ? (
                            <NewOfferPriceHint>{t('A monthly Personalized Ads checkout link will be generated from the recurring price after discount, before VAT, when you continue.')}</NewOfferPriceHint>
                        ) : null}
                        {!hasRecurringMarketingServices && !recurringPaymentUsesBankTransfer ? (
                            <NewOfferPriceRow>
                                <p>{hasMixedPaymentTypes ? t('Recurring payment link') : t('Payment link')}</p>
                                <Input_
                                    type="url"
                                    placeholder={t('https://example.com/pay')}
                                    value={recurringPaymentLinkUrl}
                                    onChange={(event) => {
                                        setRecurringPaymentLinkUrl(event.target.value);
                                        setRecurringPaymentLinkUrlError('');
                                    }}
                                    required
                                />
                            </NewOfferPriceRow>
                        ) : null}
                    </>
                ) : null}
                {hasOneOffServices && !hasOneOffOnlyServices ? (
                    <NewOfferPriceRow>
                        <p>{hasMixedPaymentTypes ? t('One-off payment link') : t('Payment link')}</p>
                        <Input_
                            type="url"
                            placeholder={t('https://example.com/pay')}
                            value={oneOffPaymentLinkUrl}
                            onChange={(event) => {
                                setOneOffPaymentLinkUrl(event.target.value);
                            }}
                        />
                    </NewOfferPriceRow>
                ) : null}
                {recurringPaymentLinkUrlError ? <NewOfferPriceError>{recurringPaymentLinkUrlError}</NewOfferPriceError> : null}
                {hasOneOffOnlyServices ? (
                    <NewOfferPriceHint>{t('One-off offers use bank transfer by default.')}</NewOfferPriceHint>
                ) : null}
                {hasRecurringServices && recurringPaymentUsesBankTransfer ? (
                    <NewOfferPriceHint>{t('Recurring offer payment method is bank transfer.')}</NewOfferPriceHint>
                ) : null}
                {hasOneOffServices && !hasOneOffOnlyServices && !oneOffPaymentLinkUrl.trim().length ? (
                    <NewOfferPriceHint>{t('No one-off link provided: one-off payment method will be bank transfer.')}</NewOfferPriceHint>
                ) : null}
                {hasMixedPaymentTypes ? (
                    <>
                        <NewOfferPriceRow>
                            <p>{t('Recurring total new price')}</p>
                            <Input_
                                placeholder={t('Total new price')}
                                type="number"
                                step="0.01"
                                value={mixedRecurringTotalValue}
                                onChange={(event) => setMixedRecurringTotalValue(event.target.value)}
                            />
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('One-off total new price')}</p>
                            <Input_
                                placeholder={t('Total new price')}
                                type="number"
                                step="0.01"
                                value={mixedOneOffTotalValue}
                                onChange={(event) => setMixedOneOffTotalValue(event.target.value)}
                            />
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('Recurring net amount')}</p>
                            <h5>€ {recurringSubtotalAfterDiscount.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('One-off net amount')}</p>
                            <h5>€ {oneOffSubtotalAfterDiscount.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('Recurring VAT amount')}</p>
                            <h5>€ {recurringVatAmount.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('One-off VAT amount')}</p>
                            <h5>€ {oneOffVatAmount.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('Recurring total + VAT')}</p>
                            <h3>€ {recurringTotalWithVat.toFixed(2)}</h3>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('One-off total + VAT')}</p>
                            <h3>€ {oneOffTotalWithVat.toFixed(2)}</h3>
                        </NewOfferPriceRow>
                    </>
                ) : (
                    <>
                        <NewOfferPriceRow>
                            <p>{t('VAT amount')}</p>
                            <h5>€ {vatAmount.toFixed(2)}</h5>
                        </NewOfferPriceRow>
                        <NewOfferPriceRow>
                            <p>{t('Total price + VAT')}</p>
                            <h1>€ {total.toFixed(2)}</h1>
                        </NewOfferPriceRow>
                    </>
                )}
            </FormBox>
            </> : null}
            {generatingCheckoutLink ? <Partial_Loader></Partial_Loader> : null}
            <Actions_>
                <Grey_
                    text={t('Back')}
                    iconLeft="Back"
                    onClick={props.onBack}
                    disabled={generatingCheckoutLink}
                />
                <Main_
                    text={generatingCheckoutLink ? t('Generating checkout link...') : t('Next')}
                    iconRight="Next"
                    onClick={go_to_next}
                    disabled={generatingCheckoutLink}
                />
            </Actions_>
        </NewOfferPrice>
    );
}

export { New_Offer_Price };
